import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Database, Calendar, Globe, Eye, Download, RefreshCw, AlertTriangle, CheckCircle, ChevronRight, Brain, Clock, XCircle, Zap } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface CollectedData {
  id: string;
  task_id: string;
  execution_id: string;
  source_url: string;
  data_type?: string;
  raw_data: any;
  processed_data?: any;
  metadata?: any;
  hash?: string;
  is_valid: boolean;
  collected_at: string;
  created_at: string;
  ai_validation_status?: 'pending' | 'processing' | 'success' | 'failed' | 'error';
  ai_validation_score?: number;
  ai_validation_reason?: string;
  ai_validation_details?: any;
  ai_validated_at?: string;
  ai_validation_model?: string;
  scrape_task_executions?: {
    id: string;
    execution_number: number;
    status: string;
    started_at: string;
  };
}

interface TaskInfo {
  id: string;
  name: string;
  description?: string;
  target_url: string;
  status: string;
  created_at: string;
}

const ScrapeTaskDataViewer: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const taskName = location.state?.taskName || '未知任务';

  const [taskInfo, setTaskInfo] = useState<TaskInfo | null>(null);
  const [collectedData, setCollectedData] = useState<CollectedData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (taskId) {
      fetchTaskInfo();
      fetchTaskData();
    }
  }, [taskId]);

  const fetchTaskInfo = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=task_get&id=${taskId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setTaskInfo(result.data);
      }
    } catch (error) {
      console.error('获取任务信息失败:', error);
    }
  };

  const fetchTaskData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: 1,
          limit: 100,
          task_id: taskId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 按采集时间倒序排列，最新的在前面
        const sortedData = (result.data.data || []).sort((a: CollectedData, b: CollectedData) => 
          new Date(b.collected_at).getTime() - new Date(a.collected_at).getTime()
        );
        setCollectedData(sortedData);
      } else {
        console.error('获取采集数据失败:', result.error);
        toast.error('获取采集数据失败');
      }
    } catch (error) {
      console.error('获取采集数据失败:', error);
      toast.error('获取采集数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          task_id: taskId,
          format: 'json'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 创建下载链接
        const dataStr = JSON.stringify(result.data.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `task_${taskId}_data_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('数据导出成功');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('导出数据失败:', error);
      toast.error('导出失败：' + (error as Error).message);
    }
  };

  const handleBack = () => {
    navigate('/admin/scrape');
  };

  const handleViewDetail = (dataId: string) => {
    navigate(`/admin/scrape/data/${taskId}/detail/${dataId}`);
  };

  const getAIValidationStatusDisplay = (status?: string, score?: number) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="w-3 h-3 mr-1" />
          未验证
        </span>
      );
    }

    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            待验证
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Zap className="w-3 h-3 mr-1 animate-pulse" />
            验证中
          </span>
        );
      case 'success':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Brain className="w-3 h-3 mr-1" />
            验证通过 {score !== undefined && `(${(score * 100).toFixed(0)}%)`}
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            验证失败
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            验证错误
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Clock className="w-3 h-3 mr-1" />
            未知状态
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回任务列表
          </button>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Database className="w-6 h-6 mr-2 text-blue-600" />
                {taskName} - 采集数据
              </h1>
              {taskInfo && (
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <div className="flex items-center">
                    <Globe className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span className="truncate max-w-[400px]" title={taskInfo.target_url}>{taskInfo.target_url}</span>
                  </div>
                  {taskInfo.description && (
                    <div>{taskInfo.description}</div>
                  )}
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    创建时间: {new Date(taskInfo.created_at).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchTaskData}
                disabled={loading}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                title="刷新数据"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </button>
              <button
                onClick={handleExportData}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <Download className="w-4 h-4 mr-2" />
                导出数据
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 数据统计 */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-blue-600">总数据量</p>
              <p className="text-2xl font-bold text-blue-900">{collectedData.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-green-600">有效数据</p>
              <p className="text-2xl font-bold text-green-900">
                {collectedData.filter(d => d.is_valid).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-red-600">无效数据</p>
              <p className="text-2xl font-bold text-red-900">
                {collectedData.filter(d => !d.is_valid).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <Brain className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-emerald-600">AI验证通过</p>
              <p className="text-2xl font-bold text-emerald-900">
                {collectedData.filter(d => d.ai_validation_status === 'success').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-purple-600">最新采集</p>
              <p className="text-sm font-bold text-purple-900">
                {collectedData.length > 0
                  ? new Date(collectedData[0].collected_at).toLocaleDateString()
                  : '无数据'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 数据列表 */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">采集数据列表</h2>
          <p className="text-sm text-gray-600 mt-1">按采集时间倒序排列，最新数据在前。点击任意行查看详情</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  来源URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  数据类型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  数据状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AI验证状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  执行次数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  采集时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {collectedData.map((data) => (
                <tr
                  key={data.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleViewDetail(data.id)}
                >
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="max-w-[300px] truncate" title={data.source_url}>
                      {data.source_url}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {data.data_type && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {data.data_type}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      data.is_valid
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {data.is_valid ? (
                        <><CheckCircle className="w-3 h-3 mr-1" />有效</>
                      ) : (
                        <><AlertTriangle className="w-3 h-3 mr-1" />无效</>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {getAIValidationStatusDisplay(data.ai_validation_status, data.ai_validation_score)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {data.scrape_task_executions ? `#${data.scrape_task_executions.execution_number}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(data.collected_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-blue-600" />
                      <span className="text-xs text-blue-600">查看详情</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {collectedData.length === 0 && (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">该任务暂无采集数据</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScrapeTaskDataViewer;